import { SkeletonBodyText, SkeletonDisplayText } from "@shopify/polaris";

import { useAdminLoading } from "../utils/adminLoading";

/**
 * The loading picture for /data-privacy, rendered at the Suspense fallback in
 * components/App.jsx while the route chunk downloads.
 *
 * routes/data-privacy.jsx has NO early-return loading branch — it paints the page
 * immediately and gates two pieces inline (a "Loading shop information..." banner
 * at :177 and an `<s-spinner>` over the exports list at :201). So this component
 * is only the chunk-load frame, and the thing it must match is the SETTLED page,
 * not some intermediate state.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THIS IS NOT `<PageSkeleton sections={[…]}/>` ANY MORE
 * ─────────────────────────────────────────────────────────────────────────────
 * PageSkeleton draws a heading plus anonymous grey text blobs. That is the right
 * shape for a page that is a plain stack of prose sections; /data-privacy is not.
 * Its middle section is a LIST OF EXPORT ROWS — a bordered card per export with a
 * two-line label on the left and a status badge plus action buttons on the right —
 * and a text blob in that slot collapses to a different height and a different
 * internal rhythm than what lands, which is the layout jump this whole family of
 * components exists to remove. The sections are also individually TITLED, and the
 * titles are static strings we know at build time, so they are drawn as real
 * headings rather than skeleton bars: same argument PageSkeleton makes for the
 * page `heading` (it is the anchor that makes the handoff invisible), applied one
 * level down.
 *
 * The previous docstring here described the sections as "the compliance summary,
 * the data-export request block, then the deletion/retention block". None of those
 * are sections of this page — see the map below. It was wrong prose over a wrong
 * shape; both are fixed together.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT THE REAL PAGE IS (routes/data-privacy.jsx)
 * ─────────────────────────────────────────────────────────────────────────────
 *   :164  <s-page heading="Data Privacy & GDPR Compliance">   ← mirrored, byte-identical
 *   :166  <s-stack direction="block" gap="large">             ← mirrored
 *   :167  subdued one-line intro paragraph                    ← mirrored
 *   :170  <s-section heading="Request Your Data Export">      ← mirrored
 *           :172  two-line explanatory paragraph
 *           :181  a shop banner (two short lines)
 *           :188  a primary "Request Data Export" button
 *   :200  <s-section heading="Your Data Exports">             ← mirrored
 *           :206  one bordered row per export: left = name + requested-at,
 *                 right = status badge + Details (+ Download) buttons
 *   :268  <s-section heading="About NudgeSmart Data Privacy"> ← mirrored
 *           :270  one-line paragraph
 *           :272  4-item list  ·  :279 sub-heading  ·  :282 4-item list
 *
 * ⚠️ `heading` must stay byte-identical to routes/data-privacy.jsx:164's own
 * `<s-page heading="Data Privacy & GDPR Compliance">`, and each `s-section
 * heading` byte-identical to its counterpart above. Nothing enforces this — keep
 * them in step by hand.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * DELIBERATELY NOT DRAWN
 * ─────────────────────────────────────────────────────────────────────────────
 * • The two `<s-modal>`s (:294, :332). They are overlays, not page frame — they
 *   occupy no layout until the merchant opens them.
 * • The `slot="breadcrumb-actions"` "Back" link (:165). It renders in the admin's
 *   own title bar, which is a fixed-height chrome region outside the body flow, so
 *   omitting it shifts nothing — and a skeleton must not paint a link that looks
 *   clickable and goes nowhere.
 * • The `fetchingShop` info banner (:177) as a SEPARATE element. It and the
 *   settled shop banner (:181) are mutually exclusive and occupy the same slot, so
 *   one banner-shaped block covers both branches.
 * • The empty-state banner (:263). It is the same slot as the export rows; three
 *   rows is the representative shape for a list section.
 *
 * NOTE ON PRIMITIVES: SkeletonDisplayText / SkeletonBodyText are Polaris REACT
 * holdouts — there is no `s-*` skeleton web component. See PageSkeleton.jsx for
 * why they survive vite-plugins/ns-polaris-css-purge.mjs. Everything structural is
 * a Polaris web component.
 */
export default function DataPrivacySkeleton() {
  // This component no longer renders <PageSkeleton>, which is where the call used
  // to live — so it must make the claim itself or Shopify's header progress bar
  // silently stops working for this route. Zero-arg and unconditional by design;
  // see utils/adminLoading.js.
  useAdminLoading();

  return (
    <s-page heading="Data Privacy & GDPR Compliance">
      <s-stack direction="block" gap="large">
        {/* :167 — the subdued one-line intro under the page heading. */}
        <SkeletonBodyText lines={1} />

        {/* :170 — "Request Your Data Export": prose, a shop banner, a button. */}
        <s-section heading="Request Your Data Export">
          <s-stack direction="block" gap="small">
            {/* :172 — the two-line explanation of what an export contains. */}
            <SkeletonBodyText lines={2} />

            {/* :177 / :181 — whichever banner is showing; both are ~two lines. */}
            <s-box
              padding="base"
              borderWidth="base"
              borderColor="subdued"
              borderRadius="base"
              background="subdued"
            >
              <SkeletonBodyText lines={2} />
            </s-box>

            {/* :188 — the primary "Request Data Export" button. */}
            <SkeletonDisplayText size="small" maxWidth="20ch" />
          </s-stack>
        </s-section>

        {/* :200 — "Your Data Exports": a list of bordered export rows. */}
        <s-section heading="Your Data Exports">
          <s-stack direction="block" gap="small">
            {[0, 1, 2].map((row) => (
              <s-box
                key={row}
                padding="base"
                borderWidth="base"
                borderColor="subdued"
                borderRadius="base"
              >
                <s-stack
                  direction="inline"
                  gap="base"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  {/* :215 / :218 — "Shop Data Export" over "Requested: …". */}
                  <s-box minInlineSize="220px">
                    <s-stack direction="block" gap="small-200">
                      <SkeletonDisplayText size="small" maxWidth="18ch" />
                      <SkeletonBodyText lines={1} />
                    </s-stack>
                  </s-box>

                  {/* :239 status badge + :248 Details button. */}
                  <s-stack direction="inline" gap="small" alignItems="center">
                    <SkeletonDisplayText size="small" maxWidth="8ch" />
                    <SkeletonDisplayText size="small" maxWidth="10ch" />
                  </s-stack>
                </s-stack>
              </s-box>
            ))}
          </s-stack>
        </s-section>

        {/* :268 — "About NudgeSmart Data Privacy": prose, list, sub-heading, list. */}
        <s-section heading="About NudgeSmart Data Privacy">
          <s-stack direction="block" gap="base">
            {/* :270 */}
            <SkeletonBodyText lines={1} />
            {/* :272 — four bulleted commitments. */}
            <SkeletonBodyText lines={4} />
            {/* :279 — "Your Rights Under GDPR:" */}
            <SkeletonDisplayText size="small" maxWidth="24ch" />
            {/* :282 — four bulleted rights. */}
            <SkeletonBodyText lines={4} />
          </s-stack>
        </s-section>
      </s-stack>
    </s-page>
  );
}
